import { action } from '@ember/object';
import { guidFor } from '@ember/object/internals';
import type Owner from '@ember/owner';
import { debounce } from '@ember/runloop';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import type {
	BindValidationFunction,
	DSSetup,
	MetricSelectOptions,
	Service,
	ValidationErrors,
	ValidationFunction,
} from 'webapp-types';
import { MetricFilterOperator, Size } from 'webapp/enums/enums';
import { validateForm } from 'webapp/utils/validate';

import didInsert from 'webapp/modifiers/did-insert';
import didUpdate from 'webapp/modifiers/did-update';
import overflowAttribute from 'webapp/modifiers/overflow-attribute';
import willDestroy from 'webapp/modifiers/will-destroy';
import FormMetricSelectComponent from 'webapp/components/form/metric-select/index';
import type { Options } from 'webapp/components/form/select-attributes';
import FormSelectAttributesComponent from 'webapp/components/form/select-attributes';
import FormSelectDimensionsComponent from 'webapp/components/form/select-dimensions';
import FormSelectUnitsComponent from 'webapp/components/form/select-units';
import RowErrorComponent from 'webapp/components/row-error';

import type DateRange from 'webapp/models-custom/date-range';
import type { Props as MetricSetupProps } from 'webapp/models-custom/metric-setup';
import MetricSetup from 'webapp/models-custom/metric-setup';

type ModelErrors = ValidationErrors<'key' | 'dimensions' | 'filtersData' | 'unitName'>;

interface FormOptions {
	legend?: string;
	attributesInOneRow?: boolean;
	showErrorMessages?: boolean;
	dateRangeForQuery?: DateRange;
	// currently unused options below
	hideMetric?: boolean;
	metricLabel?: string;
	dimensionLabel?: string;
	attributesLabel?: string;
	unitLabel?: string;
}

interface Args {
	model: DSSetup.MetricSetupData | null;
	changeMetricSetupData: (newValue: DSSetup.MetricSetupData) => void;
	validationBinder?: BindValidationFunction; // Get own validation errors from Parent
	isDisabled?: boolean;
	requireSingleOutput?: boolean;
	onContentChange?: () => void; // Update parent view, ex: call reposition if parent is popup

	metricSelectOptions?: MetricSelectOptions;
	formOptions?: FormOptions;
}

export default class CommonMetricAttributesUnitFieldsetComponent extends Component<Args> {
	@service('model-manager/metrics') declare mmMetricsService: Service.ModelManager.Metrics;
	@service('model-manager/horizon-metrics')
	declare mmHorizonMetricsService: Service.ModelManager.HorizonMetrics;

	@tracked metricSetup: MetricSetup;

	@tracked errors: ModelErrors = {};

	disableNotConnected: boolean;
	skipGoals: boolean;
	size: string;
	disableMetricSelect: boolean;
	hideConnectBtn: boolean;
	hideNoResultsActions: boolean;

	uuid = guidFor(this);

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		// metricSelectOptions
		this.disableNotConnected = args.metricSelectOptions?.disableNotConnected ?? true;
		this.skipGoals = args.metricSelectOptions?.skipGoals ?? false;
		this.size = args.metricSelectOptions?.size ?? Size.BIG;
		this.disableMetricSelect = args.metricSelectOptions?.disableMetricSelect ?? false;
		this.hideNoResultsActions = args.metricSelectOptions?.hideNoResultsActions ?? false;
		this.hideConnectBtn = args.metricSelectOptions?.hideConnectBtn ?? false;

		const model = this.args.model;
		const metricSetupProps: MetricSetupProps = {
			key: model?.key || '',
			spaceAccessId: model?.spaceAccessId || null,
			dimensions: model?.dimensions || [],
			filters: model?.filters,
			allowAllUnits: !this.args.requireSingleOutput,
			allowAllDimensionValues: !this.args.requireSingleOutput,
		};

		this.metricSetup = new MetricSetup(metricSetupProps, () => {
			debounce(this.changeMetricSetupData, 100);
		});

		if (this.args.validationBinder) {
			// Export own simple validator function, so the same logic doesn't have to repeat in all parent components.
			this.args.validationBinder(this.validate);
		}
	}

	get name() {
		return 'name-' + this.uuid;
	}

	get isDisabled() {
		return this.args.isDisabled || this.disableMetricSelect || false;
	}

	get allowMultipleOutputs() {
		return !this.args.requireSingleOutput;
	}

	get isValid() {
		return Object.keys(this.errors).length === 0;
	}

	get loadValueOptions() {
		return this.metricSetup.metric?.isTypeHorizon ? this.loadHorizonValueOptions : undefined;
	}

	validate: ValidationFunction = () => {
		const rules: { [key in keyof ModelErrors]: string[] } = {};

		if (this.args.requireSingleOutput) {
			if (this.metricSetup.mustSelectDimension) {
				rules.dimensions = ['required'];
			}
			if (this.metricSetup.isDimensionSelected) {
				rules.filtersData = ['required'];
			}
			if (this.metricSetup.hasUnits) {
				rules.unitName = ['required'];
			}
		}

		this.errors = validateForm(this.metricSetup, rules);

		return this.errors;
	};

	setOptimalUnit() {
		const metricSetup = this.metricSetup;
		if (!metricSetup.allowUnitNameFilter || metricSetup.specificSelectedUnit) {
			return;
		}

		const metricData = metricSetup.metricData;
		if (!metricData) {
			return;
		}

		const prevKey = metricSetup.key,
			prevUnit = metricSetup.unitName;

		void this.mmMetricsService
			.getOptimalUnit(metricData, undefined, undefined, metricSetup.allowAllUnits)
			.then((unit) => {
				// check if user has not changed the metric inbetween
				if (prevKey !== metricSetup.key) {
					return;
				}

				if (prevUnit !== metricSetup.unitName) {
					return;
				}

				this.changeUnit(unit);
			})
			.catch();
	}

	@action
	onOutsideModelChange() {
		const model = this.args.model;
		const newMetricSetup = new MetricSetup({
			key: model?.key || '',
			spaceAccessId: model?.spaceAccessId || null,
			dimensions: model?.dimensions || [],
			filters: model?.filters,
		});

		if (!this.metricSetup.isMatchingMetricSetup(newMetricSetup)) {
			this.metricSetup.setFromMetricSetupData(newMetricSetup.metricSetupData);
		}
	}

	@action
	changeMetricData(metricData: DSSetup.MetricData): void {
		this.metricSetup.setFromMetricData(metricData);

		if (this.args.requireSingleOutput) {
			this.setOptimalUnit();
		}

		this.changeMetricSetupData();
	}

	@action
	changeDimensions(dimensions: string[]): void {
		this.metricSetup.dimensions = dimensions;
		this.metricSetup.resetFilters();

		this.changeMetricSetupData();
	}

	@action
	changeAttributes(value: string | string[] | null): void {
		const arrayValue = this.allowMultipleOutputs
			? (value as string[])
			: value
				? ([value] as string[])
				: [];

		this.metricSetup.setFiltersFromAttributeNames(arrayValue);

		this.changeMetricSetupData();
	}

	@action
	changeUnit(value: string | null): void {
		this.metricSetup.unitName = value;

		this.changeMetricSetupData();
	}

	@action
	changeMetricSetupData() {
		this.args.changeMetricSetupData(
			this.metricSetup.metricSetupData || {
				key: '',
				spaceAccessId: null,
			},
		);
	}

	@action
	loadHorizonValueOptions(): Promise<Options> {
		return this.mmHorizonMetricsService.getValueOptionsForField(
			this.metricSetup,
			this.metricSetup.dimensions[0] || '',
			MetricFilterOperator.ANY_OF,
			this.args.formOptions?.dateRangeForQuery,
		);
	}

	@action
	triggerContentChange(): void {
		if (this.args.onContentChange) {
			this.args.onContentChange();
		}
	}

	// ------- dimension values dropdown - when multi but in one text (e.g. metric insights) -----

	private select2AttributesEvent: Event | null = null;
	attributeSelectorNode: HTMLElement | null = null;

	private get attributesChangeEventName() {
		return this.args.formOptions?.attributesInOneRow ? 'select2-attributes-change' : null;
	}

	get attributesChangeEventNames() {
		return this.attributesChangeEventName ? [this.attributesChangeEventName] : [];
	}

	@action
	bindAttributeChangeEvent(element: HTMLElement): void {
		if (!this.select2AttributesEvent) {
			const attributesChangeEventName = this.attributesChangeEventName;
			if (!attributesChangeEventName) {
				return;
			}

			this.select2AttributesEvent = new Event(attributesChangeEventName);
		}

		this.attributeSelectorNode = element;

		const dispatchAttributesChangeEvent = () => {
			const event = this.select2AttributesEvent;
			if (event) {
				this.attributeSelectorNode?.dispatchEvent(event);
			}
		};

		$(element).find('select').on('change', dispatchAttributesChangeEvent); // Any change, not just 'change.select2'
	}

	@action
	unbindAttributeChangeEvent(): void {
		if (this.attributeSelectorNode) {
			$(this.attributeSelectorNode).find('select').off('change');
		}
	}
	<template>
		<div class="metric-attributes-unit-fieldset" {{didUpdate this.onOutsideModelChange @model}}>
			<fieldset name={{this.name}}>
				{{#if @formOptions.legend}}
					<legend>{{@formOptions.legend}}</legend>
				{{/if}}

				{{#unless @formOptions.hideMetric}}
					<div class="metrickey">
						{{#if @formOptions.metricLabel}}
							<label for="{{this.name}}-metrickey">
								{{@formOptions.metricLabel}}
							</label>
						{{/if}}

						<div class="row">
							<FormMetricSelectComponent
								@selected={{this.metricSetup.metricData}}
								@onChange={{this.changeMetricData}}
								@disableNotConnected={{this.disableNotConnected}}
								@skipGoals={{this.skipGoals}}
								@isDisabled={{this.isDisabled}}
								@size={{this.size}}
								@origin={{@metricSelectOptions.origin}}
								@hideConnectBtn={{this.hideConnectBtn}}
								@hideNoResultsActions={{this.hideNoResultsActions}}
							/>

							<RowErrorComponent @items={{this.errors.key}} />
							{{#if @formOptions.showErrorMessages}}
								{{#each this.errors.key as |error|}}
									<p class="error-message">{{error.message}}</p>
								{{/each}}
							{{/if}}
						</div>
					</div>
				{{/unless}}

				{{#if this.metricSetup.allowDimensions}}
					<div class="dimensions">

						{{#if @formOptions.dimensionLabel}}
							<label for="{{this.name}}-dimensions">
								{{@formOptions.dimensionLabel}}
							</label>
						{{/if}}

						<div class="row">
							<FormSelectDimensionsComponent
								@metricSetup={{this.metricSetup}}
								@onChange={{this.changeDimensions}}
							/>

							<RowErrorComponent @items={{this.errors.dimensions}} />
							{{#if @formOptions.showErrorMessages}}
								{{#each this.errors.dimensions as |error|}}
									<p class="error-message">{{error.message}}</p>
								{{/each}}
							{{/if}}
						</div>
					</div>
				{{/if}}
				{{#if this.metricSetup.allowBasicDimensionValueFilters}}
					<div
						class="attributes"
						{{didInsert this.triggerContentChange}}
						{{willDestroy this.triggerContentChange}}
					>

						{{#if @formOptions.attributesLabel}}
							<label for="{{this.name}}-attributes">
								{{@formOptions.attributesLabel}}
							</label>
						{{/if}}
						<div class="row">
							<div
								class="attribute-wrapper"
								{{didInsert this.bindAttributeChangeEvent}}
								{{willDestroy this.unbindAttributeChangeEvent}}
								{{overflowAttribute
									selector=".active-floatlabel"
									timeout=1000
									events=this.attributesChangeEventNames
								}}
							>
								{{#if this.allowMultipleOutputs}}
									<FormSelectAttributesComponent
										@selected={{this.metricSetup.attributeNamesFromBasicFilters}}
										@metricSetupForQuery={{this.metricSetup}}
										@dateRangeForQuery={{@formOptions.dateRangeForQuery}}
										@onChange={{this.changeAttributes}}
										@isDisabled={{@isDisabled}}
										@isMultiple={{true}}
										@loadOptions={{this.loadValueOptions}}
									/>
								{{else}}
									<FormSelectAttributesComponent
										@selected={{this.metricSetup.firstAttributeNameFromBasicFilters}}
										@metricSetupForQuery={{this.metricSetup}}
										@dateRangeForQuery={{@formOptions.dateRangeForQuery}}
										@onChange={{this.changeAttributes}}
										@isDisabled={{@isDisabled}}
										@isMultiple={{false}}
										@loadOptions={{this.loadValueOptions}}
									/>
								{{/if}}
							</div>

							<RowErrorComponent @items={{this.errors.filtersData}} />
							{{#if @formOptions.showErrorMessages}}
								{{#each this.errors.filtersData as |error|}}
									<p class="error-message">{{error.message}}</p>
								{{/each}}
							{{/if}}
						</div>
					</div>
				{{/if}}

				{{#if this.metricSetup.hasUnits}}
					<div
						class="unit"
						{{didInsert this.triggerContentChange}}
						{{willDestroy this.triggerContentChange}}
					>

						{{#if @formOptions.unitLabel}}
							<label for="{{this.name}}-unit">{{@formOptions.unitLabel}}</label>
						{{/if}}

						<div class="row">
							<FormSelectUnitsComponent
								@selected={{this.metricSetup.selectedUnit}}
								@metricSetupForQuery={{this.metricSetup}}
								@dateRangeForQuery={{@formOptions.dateRangeForQuery}}
								@onChange={{this.changeUnit}}
								@isDisabled={{@isDisabled}}
								@allowAllUnits={{this.allowMultipleOutputs}}
							/>

							<RowErrorComponent @items={{this.errors.unitName}} />
							{{#if @formOptions.showErrorMessages}}
								{{#each this.errors.unitName as |error|}}
									<p class="error-message">{{error.message}}</p>
								{{/each}}
							{{/if}}
						</div>
					</div>
				{{/if}}
			</fieldset>
		</div>
	</template>
}
